import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Avatar,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tab,
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccountCircle as AccountCircleIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { 
  getAllAssessors,
  getAssessorById,
  updateAssessorDetails,
  toggleAssessorStatus,
  getAssessorStatistics
} from '../../services/api';
import PageTitle from '../../components/PageTitle';
import {
  encryptText,
} from "../../utils/encryption";

const AssessorEditManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [assessors, setAssessors] = useState([]);
  const [filteredAssessors, setFilteredAssessors] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [nablAccreditedFilter, setNablAccreditedFilter] = useState('');
  const [fieldTypeFilter, setFieldTypeFilter] = useState('');
  
  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    approved: 0
  });

  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAssessor, setSelectedAssessor] = useState(null);

  // Confirmation Dialog
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    assessor: null,
    newStatus: ''
  });

  const statusOptions = [
    { value: 'Active', label: 'Active', color: 'success' },
    { value: 'InActive', label: 'InActive', color: 'default' },
    { value: 'Suspend', label: 'Suspend', color: 'warning' }
  ];
  
  useEffect(() => {
    fetchAssessors();
    fetchStatistics();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [assessors, nameFilter, statusFilter, nablAccreditedFilter, fieldTypeFilter]);

  const fetchAssessors = async () => {
    try {
      setLoading(true);
      const response = await getAllAssessors();
      if (response.data.success) {
        setAssessors(response.data.data || []);
        setTotalCount(response.data.data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching assessors:', error);
      setErrorMessage('Failed to fetch assessors');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await getAssessorStatistics();
      if (response.data.success) {
        setStatistics(response.data.data || {
          total: 0,
          active: 0,
          inactive: 0,
          available: 0
        });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const applyFilters = () => {
    let filtered = assessors;

    if (nameFilter) {
      filtered = filtered.filter(assessor =>
        assessor.LoginName?.toLowerCase().includes(nameFilter.toLowerCase()) ||
        assessor.Name?.toLowerCase().includes(nameFilter.toLowerCase()) ||
        assessor.OfficeEmail?.toLowerCase().includes(nameFilter.toLowerCase()) ||
        assessor.ResidenceEmail?.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(assessor => assessor.CurrentStatus === statusFilter);
    }

    if (nablAccreditedFilter) {
      if (nablAccreditedFilter === 'Pending') {
        filtered = filtered.filter(assessor => 
          !assessor.orgNABLaccridiated || 
          assessor.orgNABLaccridiated === '' || 
          assessor.orgNABLaccridiated === null
        );
      } else {
        filtered = filtered.filter(assessor => assessor.orgNABLaccridiated === nablAccreditedFilter);
      }
    }

    if (fieldTypeFilter) {
      filtered = filtered.filter(assessor => 
        assessor.assessorFieldType && 
        assessor.assessorFieldType.toLowerCase().includes(fieldTypeFilter.toLowerCase())
      );
    }

    setFilteredAssessors(filtered);
    setPage(0);
  };

  const clearFilters = () => {
    setNameFilter('');
    setStatusFilter('');
    setNablAccreditedFilter('');
    setFieldTypeFilter('');
  };

  const handleExportToExcel = async () => {
    if (filteredAssessors.length === 0) {
      setErrorMessage('No data available to export');
      return;
    }

    setExporting(true);
    
    try {
      // Prepare data for export
      const exportData = filteredAssessors.map((assessor, index) => ({
        'S. No': index + 1,
        'Assessor Id': assessor.LoginName,
        'Name': assessor.Name || '',
        'Office Email': assessor.OfficeEmail || '',
        'Office Mobile': assessor.OfficeMobile || '',
        'Capacity': assessor.Capacity || '',
        'Field Type': assessor.assessorFieldType || '',
        'Current Employer': assessor.CurrentEmployer || '',
        'NABL Accredited': assessor.orgNABLaccridiated || '',
        'Status': assessor.CurrentStatus || '',
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();

      // Set column widths for better readability
      const colWidths = [
        { wch: 8 },  // S. No
        { wch: 25 }, // Name
        { wch: 30 }, // Office Email
        { wch: 15 }, // Office Mobile
        { wch: 30 }, // Current Employer
        { wch: 18 }, // NABL Accredited
        { wch: 12 }, // Status
      ];

      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Assessors');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `Assessors_Export_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      setSuccessMessage(`Excel file exported successfully: ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setErrorMessage('Failed to export Excel file');
    } finally {
      setExporting(false);
    }
  };

  const handleMenuOpen = (event, assessor) => {
    setAnchorEl(event.currentTarget);
    setSelectedAssessor(assessor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAssessor(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChangeRequest = (assessor, newStatus) => {
    setConfirmDialog({
      open: true,
      assessor: assessor,
      newStatus: newStatus
    });
  };

  const handleConfirmStatusChange = async () => {
    const { assessor, newStatus } = confirmDialog;
    setConfirmDialog({ open: false, assessor: null, newStatus: '' });
    
    try {
      setLoading(true);
      
      const response = await toggleAssessorStatus(assessor.OrgId, {
        status: newStatus,
        updatedBy: localStorage.getItem('orgId')
      });

      if (response.data.success) {
        setSuccessMessage(`Assessor status updated to ${newStatus} successfully`);
        // Reload assessors to reflect changes
        await fetchAssessors();
      } else {
        setErrorMessage('Failed to update assessor status');
      }
    } catch (error) {
      console.error('Error updating assessor status:', error);
      setErrorMessage('Error updating assessor status');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelStatusChange = () => {
    setConfirmDialog({ open: false, assessor: null, newStatus: '' });
  };


  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'success';
      case 'Suspend': return 'warning';
      case 'InActive': return 'default';
      default: return 'default';
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.split(' ');
    return nameParts.length > 1 
      ? `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };


  // Handle view profile action
    const handleAssessorEdit = (id) => {
      navigate(`/main/admin/assessor-edit?orgId=${encryptText(id)}`);
    };

    // Handle view profile action
    const handleViewProfile = (id) => {
      const url = `/main/admin/assessor-profile?AssessorId=${encryptText(id)}`;
      window.open(url, "_blank");
    };

  return (
    <Box sx={{ p: 3 }}>
       <PageTitle title="NABL | Assessor Management" />
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        Assessor Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                { totalCount }
              </Typography>
              <Typography variant="body2">Total Assessors</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {assessors.filter(a => a.CurrentStatus === 'Active').length}
              </Typography>
              <Typography variant="body2">Active Assessors</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {assessors.filter(a => (a.CurrentStatus === 'InActive' || a.CurrentStatus === null || a.CurrentStatus === undefined || a.CurrentStatus === '')).length}
              </Typography>
              <Typography variant="body2">Inactive Assessors</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, rgba(153, 33, 17, 1) 0%, rgba(239, 83, 56, 1) 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {assessors.filter(a => (a.CurrentStatus === 'Suspend')).length}
              </Typography>
              <Typography variant="body2">Suspended Assessors</Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {statistics.approved || assessors.filter(a => a.Is_Approved === 1).length}
              </Typography>
              <Typography variant="body2">Approved Assessors</Typography>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          {/* First Row - Search and Filters */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Name, Email or Username"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.25}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                {statusOptions.map(status => (
                  <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.25}>
            <FormControl fullWidth size="small">
              <InputLabel>NABL Accredited</InputLabel>
              <Select
                value={nablAccreditedFilter}
                onChange={(e) => setNablAccreditedFilter(e.target.value)}
                label="NABL Accredited"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.25}>
            <FormControl fullWidth size="small">
              <InputLabel>Field Type</InputLabel>
              <Select
                value={fieldTypeFilter}
                onChange={(e) => setFieldTypeFilter(e.target.value)}
                label="Field Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Testing">Testing</MenuItem>
                <MenuItem value="Calibration">Calibration</MenuItem>
                <MenuItem value="Medical">Medical</MenuItem>
                <MenuItem value="PTP">PTP</MenuItem>
                <MenuItem value="RMP">RMP</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Second Row/Column - Action Buttons */}
          <Grid item xs={12} md={2.25}>
            <Stack 
              direction={{ xs: 'column', sm: 'row', md: 'column' }} 
              spacing={1}
              sx={{ height: '100%' }}
            >
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#5a67d8',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                  },
                }}
              >
                Clear
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchAssessors}
                size="small"
                variant="outlined"
                fullWidth
                sx={{
                  textTransform: 'none',
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Grid>

          {/* Export Button - Full Width on Mobile */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleExportToExcel}
                size="medium"
                variant="contained"
                disabled={filteredAssessors.length === 0 || exporting}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  background: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
                  boxShadow: '0 4px 15px rgba(86, 171, 47, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4a9629 0%, #96d054 100%)',
                    boxShadow: '0 6px 20px rgba(86, 171, 47, 0.4)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                  },
                }}
              >
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>S. No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Assessor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Field Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Current Employer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Organization NABL Accredited</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                {/* <TableCell sx={{ fontWeight: 'bold' }}>Approved</TableCell> */}
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredAssessors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No assessors found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAssessors
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((assessor, index) => (
                    <TableRow key={assessor.Id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: '#667eea', 
                            width: 40, 
                            height: 40,
                            fontSize: '0.875rem'
                          }}>
                            {getInitials(assessor.Name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {assessor.Name || 'N/A'}
                            </Typography>
                            {assessor.Capacity && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Capacity: {assessor.Capacity}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {assessor.OfficeEmail && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14 }} />
                              {assessor.OfficeEmail}
                            </Typography>
                          )}
                          {assessor.OfficeMobile && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14 }} />
                              {assessor.OfficeMobile}
                            </Typography>
                          )}
                          {!assessor.OfficeEmail && !assessor.OfficeMobile && (
                            <Typography variant="body2" color="text.secondary">
                              No contact info
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200 }}>
                          {assessor.assessorFieldType ? (
                            assessor.assessorFieldType.split(',').map((type, idx) => (
                              <Chip
                                key={idx}
                                label={type.trim()}
                                size="small"
                                sx={{ 
                                  mr: 0.5, 
                                  mb: 0.5,
                                  fontSize: '0.75rem'
                                }}
                                color="primary"
                                variant="outlined"
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assessor.CurrentEmployer || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assessor.orgNABLaccridiated || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={assessor.CurrentStatus || 'InActive'}
                            onChange={(e) => handleStatusChangeRequest(assessor, e.target.value)}
                            sx={{
                              '& .MuiSelect-select': {
                                color: assessor.CurrentStatus === 'Active' ? 'success.main' : 
                                       assessor.CurrentStatus === 'Suspend' ? 'warning.main' : 'text.secondary'
                              }
                            }}
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Chip 
                                  label={option.label} 
                                  color={option.color} 
                                  size="small" 
                                  sx={{ minWidth: 80 }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      {/* <TableCell>
                        <Chip
                          label={assessor.Is_Approved === 1 ? 'Approved' : 'Pending'}
                          color={assessor.Is_Approved === 1 ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell> */}
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, assessor)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredAssessors.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleViewProfile(selectedAssessor.OrgId); handleMenuClose(); }}>
          <AccountCircleIcon sx={{ mr: 1, fontSize: 20 }} />
          View Profile
        </MenuItem>
        <MenuItem onClick={() => { handleAssessorEdit(selectedAssessor.OrgId); handleMenuClose(); }}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit Assessor
        </MenuItem>        
      </Menu>

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelStatusChange}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Status Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to change the status of <strong>{confirmDialog.assessor?.Name}</strong> to <strong>{confirmDialog.newStatus}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStatusChange} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmStatusChange} color="primary" variant="contained" autoFocus>
            Yes, Change Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessorEditManagement;